import { Component, OnInit, ViewChild } from '@angular/core';
import { BookableViewingContact, EventService } from '@app/events/shared/event.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { EventModel, EventModelUtils } from '@app/events/shared/event.model';
import { FormControl } from '@angular/forms';
import { ArrowRight, Calendar, ChevronDown, LucideAngularModule, PlusCircle, SquareChevronRight } from 'lucide-angular';
import { DatePipe, NgClass } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { PriceLockingWarningModalComponent } from '@app/partials/modal/price-locking-warning-modal.component';
import { MarkAsLostModalComponent, MarkAsLostPayload } from '@app/events/view/mark-as-lost-modal.component';
import { ViewingDateModalComponent, ViewingDatePayload } from '@app/board/view/viewing-date-modal.component';
import {
  BookNewViewingModalComponent,
  BookNewViewingPayload,
} from '@app/board/view/book-new-viewing-modal.component';
import { BoardCardActionsComponent } from '@app/board/view/board-card-actions.component';
import { HelpPanelComponent } from '@app/partials/help-panel/help-panel.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { ToastService } from '@app/shared/toast/toast.service';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { BoardService } from '@app/board/shared/board.service';
import { NewQuotesBadgeService } from '@app/board/shared/new-quotes-badge.service';
import { AuthService } from '@app/auth/auth.service';
import {
  BoardCard,
  BoardColumn,
  BoardColumnKey,
  BoardContactCard,
  BoardEventCard,
  BoardOpportunityCard,
  BoardQuoteCard,
  BoardVenueOption,
} from '@app/board/shared/board.model';
import { EventSubStatus } from '@app/events/shared/event.model';

// TVG-119: Active Opportunity sub-status filter pills, persisted in localStorage
// so the operator's selection survives reloads. Default is both pills selected
// (i.e. no filtering).
const SUB_STATUS_FILTER_STORAGE_KEY = 'tvg.board.active-opportunity.sub-status-filter';
const SUB_STATUS_VALUES: readonly EventSubStatus[] = ['event_created', 'in_conversation'] as const;

// TVG-124: Lost-column filter pills ("This quarter" / "All"). Persisted in
// localStorage so the operator's choice survives reloads.
type LostFilter = 'this-quarter' | 'all';
const LOST_FILTER_STORAGE_KEY = 'tvg.board.lost.filter';
const LOST_FILTER_VALUES: readonly LostFilter[] = ['this-quarter', 'all'] as const;

// TVG-123: Cancelled-column filter pills ("This year" / "All Cancelled").
// Same persistence pattern as Lost.
type CancelledFilter = 'this-year' | 'all';
const CANCELLED_FILTER_STORAGE_KEY = 'tvg.board.cancelled.filter';
const CANCELLED_FILTER_VALUES: readonly CancelledFilter[] = ['this-year', 'all'] as const;

@Component({
  selector: 'app-events-board',
  standalone: true,
  templateUrl: './board.component.html',
  imports: [
    LucideAngularModule,
    DatePipe,
    NgClass,
    CdkDropList,
    CdkDrag,
    BadgeComponent,
    PriceLockingWarningModalComponent,
    MarkAsLostModalComponent,
    ViewingDateModalComponent,
    BookNewViewingModalComponent,
    BoardCardActionsComponent,
    HelpPanelComponent,
  ],
})
export class BoardComponent implements OnInit {
  protected searchControl: FormControl = new FormControl('');
  protected events?: EventModel[];
  protected backlog: EventModel[] = [];
  protected loading = false;
  protected showConfirmationModal = false;
  protected pendingConfirmedDrop:
    | {
        event: EventModel;
        fromList: EventModel[];
        toList: EventModel[];
        toIndex: number;
        status: EventModel['status'];
      }
    | null = null;

  // TVG-111: Feature-toggle gated 7-column view.
  protected crmSalesFlowEnabled = false;
  protected boardColumns: BoardColumn[] = [];
  protected venueOptions: BoardVenueOption[] = [];
  protected selectedVenueIds = new Set<number>();
  protected venueFilterOpen = false;

  // TVG-119: Active Opportunity sub-status filter pills. Persisted in localStorage.
  protected readonly subStatusOptions: { value: EventSubStatus; label: string; color: string }[] = [
    { value: 'event_created', label: 'Event Created', color: 'light' },
    { value: 'in_conversation', label: 'In Conversation', color: 'warning' },
  ];
  protected selectedSubStatuses = new Set<EventSubStatus>(this.loadSubStatusFilter());

  // TVG-124: Lost-column filter pills.
  protected readonly lostFilterOptions: { value: LostFilter; label: string }[] = [
    { value: 'this-quarter', label: 'This quarter' },
    { value: 'all', label: 'All Lost' },
  ];
  protected selectedLostFilter: LostFilter = this.loadLostFilter();

  // TVG-123: Cancelled-column filter pills.
  protected readonly cancelledFilterOptions: { value: CancelledFilter; label: string }[] = [
    { value: 'this-year', label: 'This year' },
    { value: 'all', label: 'All Cancelled' },
  ];
  protected selectedCancelledFilter: CancelledFilter = this.loadCancelledFilter();

  protected swimlanes: { title: EventModel['status']; events: EventModel[] }[] = [
    { title: 'Opportunity', events: [] },
    { title: 'Provisional', events: [] },
    { title: 'Confirmed', events: [] },
    { title: 'Cancelled', events: [] },
    { title: 'Closed', events: [] },
  ];
  protected swimlaneTitles: string[] = ['Backlog', ...this.swimlanes.map((swimlane) => swimlane.title)];

  protected readonly Calendar = Calendar;
  protected readonly ChevronDown = ChevronDown;
  protected readonly EventModelUtils = EventModelUtils;

  /** TVG-356: gates the Confirmed drop target — only admins may confirm. */
  protected canConfirmEvents = false;

  constructor(
    private eventService: EventService,
    private boardService: BoardService,
    private featureToggleService: FeatureToggleService,
    private router: Router,
    private newQuotesBadgeService: NewQuotesBadgeService,
    private confirmationModal: ConfirmationModalService,
    private toastService: ToastService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    // TVG-356: only admins may confirm a booking, so the Confirmed drop target
    // is refused for everyone else rather than failing at the API.
    this.authService.getUser().subscribe({
      next: (user) => {
        this.canConfirmEvents = (user?.roles ?? []).some(
          (role: any) =>
            (typeof role === 'string' ? role : role?.name ?? '').toLowerCase() === 'admin',
        );
      },
      error: () => {
        this.canConfirmEvents = false;
      },
    });

    // TVG-331: debounce the Book New Viewing search so typing a name is one
    // refetch rather than one per keystroke.
    this.bookableSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => this.loadBookableContacts(term.trim()));

    // TVG-331: wait for loadActive() to resolve before choosing which board to
    // render. isEnabled() reads a cached map that app-layout fills in
    // asynchronously, so reading it synchronously here raced the HTTP call: on
    // a cold load of /board the map was still {} and the legacy swimlane board
    // rendered permanently, hiding the Viewing Booked column and the Book New
    // Viewing button even with crm-sales-flow on. Same race the sidebar fixed
    // in TVG-236.
    this.featureToggleService.loadActive().subscribe({
      next: () => this.selectBoardForToggleState(),
      error: () => this.selectBoardForToggleState(),
    });
  }

  private selectBoardForToggleState(): void {
    this.crmSalesFlowEnabled = this.featureToggleService.isEnabled('crm-sales-flow');
    if (this.crmSalesFlowEnabled) {
      this.loadBoardData();
      // TVG-117: opening the board clears the "new quotes" sidebar badge.
      this.boardService.markViewed().subscribe({
        next: () => this.newQuotesBadgeService.clear(),
        error: () => {},
      });
      return;
    }
    this.loadLegacyBoard();
  }

  private loadLegacyBoard(): void {
    this.loading = true;
    this.eventService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
          sort: [{ field: 'priority', dir: 'desc' }],
        }),
      )
      .subscribe((page) => {
        this.events = page.data;
        this.events.forEach((event) => {
          if (event.show_on_board) {
            const swimlane = this.swimlanes.find((s) => s.title === event.status);
            if (swimlane) {
              swimlane.events.push(event);
            }
          } else {
            this.backlog.push(event);
          }
        });
        this.loading = false;
      });
  }

  private loadBoardData(): void {
    this.loading = true;
    this.boardService.getBoard(Array.from(this.selectedVenueIds), this.selectedLostFilter, this.selectedCancelledFilter).subscribe({
      next: (data) => {
        this.boardColumns = data.columns;
        // Only set venue options on first load (don't lose them when filter is applied).
        if (this.venueOptions.length === 0) {
          this.venueOptions = data.venues;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  // TVG-124: switch the Lost-column scope and refetch.
  protected isLostFilterSelected(value: LostFilter): boolean {
    return this.selectedLostFilter === value;
  }

  protected selectLostFilter(value: LostFilter): void {
    if (this.selectedLostFilter === value) {
      return;
    }
    this.selectedLostFilter = value;
    this.persistLostFilter();
    this.loadBoardData();
  }

  private loadLostFilter(): LostFilter {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'this-quarter';
    }
    try {
      const raw = window.localStorage.getItem(LOST_FILTER_STORAGE_KEY);
      if (raw && (LOST_FILTER_VALUES as readonly string[]).includes(raw)) {
        return raw as LostFilter;
      }
    } catch {
      // ignore
    }
    return 'this-quarter';
  }

  private persistLostFilter(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(LOST_FILTER_STORAGE_KEY, this.selectedLostFilter);
    } catch {
      // ignore
    }
  }

  // TVG-123: switch the Cancelled-column scope and refetch. Mirrors Lost.
  protected isCancelledFilterSelected(value: CancelledFilter): boolean {
    return this.selectedCancelledFilter === value;
  }

  protected selectCancelledFilter(value: CancelledFilter): void {
    if (this.selectedCancelledFilter === value) {
      return;
    }
    this.selectedCancelledFilter = value;
    this.persistCancelledFilter();
    this.loadBoardData();
  }

  private loadCancelledFilter(): CancelledFilter {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'this-year';
    }
    try {
      const raw = window.localStorage.getItem(CANCELLED_FILTER_STORAGE_KEY);
      if (raw && (CANCELLED_FILTER_VALUES as readonly string[]).includes(raw)) {
        return raw as CancelledFilter;
      }
    } catch {
      // ignore
    }
    return 'this-year';
  }

  private persistCancelledFilter(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(CANCELLED_FILTER_STORAGE_KEY, this.selectedCancelledFilter);
    } catch {
      // ignore
    }
  }

  protected toggleVenueFilter(venueId: number): void {
    if (this.selectedVenueIds.has(venueId)) {
      this.selectedVenueIds.delete(venueId);
    } else {
      this.selectedVenueIds.add(venueId);
    }
    this.loadBoardData();
  }

  protected clearVenueFilter(): void {
    if (this.selectedVenueIds.size === 0) {
      return;
    }
    this.selectedVenueIds.clear();
    this.loadBoardData();
  }

  protected isVenueSelected(venueId: number): boolean {
    return this.selectedVenueIds.has(venueId);
  }

  protected venueFilterLabel(): string {
    if (this.selectedVenueIds.size === 0) {
      return 'All venues';
    }
    if (this.selectedVenueIds.size === 1) {
      const id = Array.from(this.selectedVenueIds)[0];
      return this.venueOptions.find((v) => v.id === id)?.name ?? `${this.selectedVenueIds.size} venue`;
    }
    return `${this.selectedVenueIds.size} venues`;
  }

  protected toggleVenueFilterDropdown(): void {
    this.venueFilterOpen = !this.venueFilterOpen;
  }

  protected closeVenueFilterDropdown(): void {
    this.venueFilterOpen = false;
  }

  // TVG-119: Filter cards for the Active Opportunity column by the user's
  // selected sub-status pills. Other columns pass through untouched. When no
  // pill is selected the column is intentionally empty — same semantics as
  // unchecking every checkbox in the venue filter.
  protected visibleCardsFor(column: BoardColumn): BoardColumn['cards'] {
    if (column.key !== 'active-opportunity') {
      return column.cards;
    }

    return column.cards.filter((card) => {
      if (card.kind !== 'opportunity') {
        return true;
      }
      const sub = card.sub_status;
      if (!sub) {
        return this.selectedSubStatuses.size === SUB_STATUS_VALUES.length;
      }
      return this.selectedSubStatuses.has(sub);
    });
  }

  protected isSubStatusSelected(value: EventSubStatus): boolean {
    return this.selectedSubStatuses.has(value);
  }

  protected toggleSubStatusFilter(value: EventSubStatus): void {
    if (this.selectedSubStatuses.has(value)) {
      this.selectedSubStatuses.delete(value);
    } else {
      this.selectedSubStatuses.add(value);
    }
    this.persistSubStatusFilter();
  }

  private loadSubStatusFilter(): EventSubStatus[] {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [...SUB_STATUS_VALUES];
    }
    try {
      const raw = window.localStorage.getItem(SUB_STATUS_FILTER_STORAGE_KEY);
      if (!raw) {
        return [...SUB_STATUS_VALUES];
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [...SUB_STATUS_VALUES];
      }
      const valid = parsed.filter((v): v is EventSubStatus => SUB_STATUS_VALUES.includes(v));
      return valid.length > 0 ? valid : [...SUB_STATUS_VALUES];
    } catch {
      return [...SUB_STATUS_VALUES];
    }
  }

  private persistSubStatusFilter(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(
        SUB_STATUS_FILTER_STORAGE_KEY,
        JSON.stringify(Array.from(this.selectedSubStatuses)),
      );
    } catch {
      // localStorage may be unavailable (private mode, quota); silently no-op.
    }
  }

  protected isContactCard(card: BoardCard): card is BoardContactCard {
    return card.kind === 'contact';
  }

  protected isQuoteCard(card: BoardCard): card is BoardQuoteCard {
    return card.kind === 'quote';
  }

  protected isOpportunityCard(card: BoardCard): card is BoardOpportunityCard {
    return card.kind === 'opportunity';
  }

  protected isEventCard(card: BoardCard): card is BoardEventCard {
    return card.kind === 'event';
  }

  /**
   * TVG-118: Freshness colour for board cards. Mirrors the rule in
   * EventModelUtils.getFreshnessColor but driven by the card's own freshness
   * timestamp so we don't need a full EventModel.
   */
  protected cardFreshnessColor(
    card: BoardEventCard | BoardOpportunityCard,
    status: BoardEventCard['status'] | null = null,
  ): 'green' | 'amber' | 'grey' | null {
    if (status === 'Confirmed' || status === 'Cancelled' || status === 'Closed') {
      return null;
    }

    const ts = card.last_prospect_activity_at;
    if (!ts) {
      return 'grey';
    }

    const activityAt = Date.parse(ts);
    if (Number.isNaN(activityAt)) {
      return 'grey';
    }

    const ageDays = (Date.now() - activityAt) / (24 * 60 * 60 * 1000);
    if (ageDays < 7) return 'green';
    if (ageDays < 30) return 'amber';
    return 'grey';
  }

  /**
   * TVG-118: `"{bride} & {groom}"` with sensible fallback per Mark's scope
   * answer. Used by Pencil / Confirmed / Lost cards.
   */
  protected coupleNames(card: BoardEventCard): string {
    const bride = card.bride?.trim();
    const groom = card.groom?.trim();
    if (bride && groom) return `${bride} & ${groom}`;
    return bride || groom || card.name;
  }

  /**
   * TVG-118: Pencil counter — days since `pencil_started_at`. Returns null if
   * the event hasn't been stamped yet (events at Provisional before the
   * migration, until their next save).
   */
  protected daysAtPencil(card: BoardEventCard): number | null {
    if (!card.pencil_started_at) return null;
    const ts = Date.parse(card.pencil_started_at);
    if (Number.isNaN(ts)) return null;
    return Math.max(0, Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000)));
  }

  /**
   * TVG-118: Days until wedding for the Confirmed card countdown. Returns the
   * raw integer; the template decides how to label it ("Today", "Tomorrow",
   * "Wedding passed", etc.). Returns null when start_date is missing.
   */
  protected daysUntilWedding(card: BoardEventCard): number | null {
    if (!card.start_date) return null;
    const ts = Date.parse(card.start_date);
    if (Number.isNaN(ts)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((ts - today.getTime()) / (24 * 60 * 60 * 1000));
  }

  /**
   * TVG-118: Format for the Viewing Booked / Confirmed countdown ribbons.
   */
  protected weddingCountdownLabel(card: BoardEventCard): string {
    const days = this.daysUntilWedding(card);
    if (days === null) return '';
    if (days < 0) return 'Wedding passed';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  }

  protected weddingCountdownUrgent(card: BoardEventCard): boolean {
    const days = this.daysUntilWedding(card);
    return days !== null && days >= 0 && days <= 1;
  }

  /**
   * TVG-121: Days from today until the viewing date (show_round_at). Used by
   * the Viewing Booked card headline countdown. Returns null when no viewing
   * is booked.
   */
  protected daysUntilViewing(card: BoardEventCard): number | null {
    if (!card.show_round_at) return null;
    const ts = Date.parse(card.show_round_at);
    if (Number.isNaN(ts)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const viewingDay = new Date(ts);
    viewingDay.setHours(0, 0, 0, 0);
    return Math.floor((viewingDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  }

  protected viewingCountdownLabel(card: BoardEventCard): string {
    const days = this.daysUntilViewing(card);
    if (days === null) return '';
    if (days < 0) return 'Awaiting outcome';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  }

  protected viewingCountdownUrgent(card: BoardEventCard): boolean {
    const days = this.daysUntilViewing(card);
    return days !== null && days >= 0 && days <= 1;
  }

  protected handleOpportunityCardClick(card: BoardOpportunityCard): void {
    this.router.navigate(['/contacts', card.contact_id, 'edit']);
  }

  protected guestTotal(card: BoardEventCard): number | null {
    const day = card.day_guests ?? 0;
    const evening = card.evening_guests ?? 0;
    const total = day + evening;
    return total > 0 ? total : null;
  }

  protected contactSourceLabel(card: BoardContactCard): string {
    if (card.source === 'Other' && card.source_other) {
      return card.source_other;
    }
    return card.source ?? 'Unknown source';
  }

  protected daysSince(iso: string | null): number | null {
    if (!iso) {
      return null;
    }
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return null;
    }
    const diffMs = Date.now() - parsed;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }

  protected formatPriceRange(min: number | null, max: number | null): string {
    if (min === null && max === null) {
      return '—';
    }
    if (min !== null && max !== null && min !== max) {
      return `${this.formatGbp(min)} – ${this.formatGbp(max)}`;
    }
    return this.formatGbp((min ?? max) as number);
  }

  private formatGbp(value: number): string {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  }

  protected columnBadgeColor(key: BoardColumnKey): string {
    switch (key) {
      case 'pre-quote':
        return 'light';
      case 'quote-received':
        return 'info';
      case 'active-opportunity':
        return 'warning';
      case 'viewing-booked':
        return 'primary';
      case 'pencil-provisional':
        return 'primary';
      case 'confirmed':
        return 'success';
      // TVG-123: completed weddings are wins; cancelled weddings sit alongside
      // Lost as a separate outcome (muted error).
      case 'completed':
        return 'light';
      case 'cancelled':
        return 'error';
      case 'lost':
        return 'error';
      default:
        return 'light';
    }
  }

  protected handleContactClick(card: BoardContactCard): void {
    this.router.navigate(['/contacts', card.id, 'edit']);
  }

  // TVG-135: the first two columns represent the contact, so a Quote Received
  // card opens the contact record when one exists. Quotes captured for an email
  // with no contact yet have no record to open, so the click stays a no-op (the
  // dedicated quote viewer is a later case in the CRM Sales Flow project).
  protected handleQuoteCardClick(card: BoardQuoteCard): void {
    this.handleOpenContact(card.contact_id);
  }

  protected handleBoardEventClick(card: BoardEventCard): void {
    this.router.navigate(['/events', card.id]);
  }

  // TVG-135: columns whose card click should land on the event (where the work
  // happens) rather than the parent contact. Active Opportunity and Viewing
  // Booked stay contact-primary (still nurturing); Lost is contact-primary for
  // re-engagement.
  private readonly eventPrimaryColumns: BoardColumnKey[] = ['pencil-provisional', 'confirmed', 'completed', 'cancelled'];

  protected cardPrimary(columnKey: BoardColumnKey): 'contact' | 'event' {
    return this.eventPrimaryColumns.includes(columnKey) ? 'event' : 'contact';
  }

  protected handleOpenContact(contactId: number | null): void {
    if (contactId !== null) {
      this.router.navigate(['/contacts', contactId, 'edit']);
    }
  }

  protected handleOpenEvent(eventId: number | null): void {
    if (eventId !== null) {
      this.router.navigate(['/events', eventId]);
    }
  }

  // TVG-135: card-body click follows the column's primary route, so the large
  // click target matches the emphasised quick-action button.
  protected handleEventCardClick(card: BoardEventCard, columnKey: BoardColumnKey): void {
    if (this.cardPrimary(columnKey) === 'event') {
      this.handleOpenEvent(card.id);
    } else {
      this.handleOpenContact(card.contact_id);
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // TVG-134: Manual drag-and-drop on the CRM Sales Flow board.
  //
  // The 7-column board renders cards bucketed from server state (status +
  // show_round); auto-promotion still moves cards on its own. A manual move
  // reuses the same endpoints the card action buttons use, then refetches so
  // cards re-bucket from the source of truth — we never do optimistic array
  // surgery across the heterogeneous card shapes.
  //
  // Draggable group: Active Opportunity, Viewing Booked, Pencil (Provisional),
  // Confirmed, Lost. Pre-Quote / Quote Received have no event yet; Completed /
  // Cancelled are terminal query-time views — none take part in this pass.
  // ───────────────────────────────────────────────────────────────────────
  protected readonly crmDropColumnIds: BoardColumnKey[] = [
    'active-opportunity',
    'viewing-booked',
    'pencil-provisional',
    'confirmed',
    'lost',
  ];

  // Capture-dialog state for a drop into Viewing Booked / Lost.
  protected viewingModalOpen = false;
  protected viewingSubmitting = false;
  private pendingViewingEventId: number | null = null;

  protected lostModalOpen = false;
  protected lostSubmitting = false;
  private pendingLostEventId: number | null = null;

  protected isCrmDropTarget(key: BoardColumnKey): boolean {
    return this.crmDropColumnIds.includes(key);
  }

  /** A card is draggable only when it maps unambiguously to a single event. */
  protected isCardDraggable(card: BoardCard, columnKey: BoardColumnKey): boolean {
    return this.eventIdForCard(card, columnKey) !== null;
  }

  private eventIdForCard(card: BoardCard, columnKey: BoardColumnKey): number | null {
    if (this.isOpportunityCard(card)) {
      return columnKey === 'active-opportunity' ? card.event_id : null;
    }
    if (this.isEventCard(card)) {
      // Only the event-flow columns are drag sources (Completed/Cancelled are views).
      return this.isCrmDropTarget(columnKey) ? card.id : null;
    }
    return null;
  }

  protected onCrmDrop(event: CdkDragDrop<BoardCard[]>): void {
    const fromKey = event.previousContainer.id as BoardColumnKey;
    const toKey = event.container.id as BoardColumnKey;
    // Same-column drops are reorders; board sort is server-defined, so ignore.
    if (fromKey === toKey || !this.isCrmDropTarget(toKey)) {
      return;
    }
    const card = event.item.data as BoardCard;
    const eventId = this.eventIdForCard(card, fromKey);
    if (eventId === null) {
      return;
    }
    this.routeCardMove(eventId, fromKey, toKey);
  }

  private routeCardMove(eventId: number, fromKey: BoardColumnKey, toKey: BoardColumnKey): void {
    switch (toKey) {
      case 'viewing-booked':
        this.pendingViewingEventId = eventId;
        this.viewingModalOpen = true;
        return;
      case 'lost':
        this.pendingLostEventId = eventId;
        this.lostModalOpen = true;
        return;
      case 'pencil-provisional':
        void this.confirmMoveToPencil(eventId);
        return;
      case 'confirmed':
        void this.confirmMoveToConfirmed(eventId);
        return;
      case 'active-opportunity':
        this.demoteToActiveOpportunity(eventId, fromKey);
        return;
      default:
        return;
    }
  }

  // Viewing Booked capture dialog -------------------------------------------
  protected submitViewingDate(payload: ViewingDatePayload): void {
    const eventId = this.pendingViewingEventId;
    if (eventId === null || this.viewingSubmitting) {
      return;
    }
    this.viewingSubmitting = true;
    this.eventService.bookViewing(eventId, payload.show_round_date, payload.show_round_time).subscribe({
      next: () => {
        this.viewingSubmitting = false;
        this.closeViewingModal();
        this.toastService.success('Viewing booked');
        this.loadBoardData();
      },
      error: () => {
        this.viewingSubmitting = false;
        this.toastService.error('Unable to book the viewing');
      },
    });
  }

  // Override — skip the viewing. No state change; the dragged card returns to
  // Active Opportunity, where the SM can drag it straight on to Pencil/Confirmed.
  protected skipViewing(): void {
    this.closeViewingModal();
  }

  private closeViewingModal(): void {
    this.viewingModalOpen = false;
    this.pendingViewingEventId = null;
  }

  // ───────────────────────────────────────────────────────────────────────
  // TVG-331: "Book New Viewing" — book a viewing for any venue without first
  // finding the card. The dialog picks the contact and the event; booking then
  // goes through the same endpoint the drag-and-drop dialog uses, so the show
  // round lands on the event (and so on the contact record) identically.
  // ───────────────────────────────────────────────────────────────────────
  @ViewChild(BookNewViewingModalComponent) private bookNewViewingModal?: BookNewViewingModalComponent;

  protected bookNewViewingOpen = false;
  protected bookNewViewingSubmitting = false;
  protected bookNewViewingLoading = false;
  protected bookableContacts: BookableViewingContact[] = [];

  /** Keystrokes from the dialog's search box, debounced before refetching. */
  private readonly bookableSearch$ = new Subject<string>();

  protected openBookNewViewing(): void {
    this.bookNewViewingOpen = true;
    this.bookNewViewingModal?.reset();
    this.loadBookableContacts('');
  }

  protected closeBookNewViewing(): void {
    this.bookNewViewingOpen = false;
    this.bookNewViewingSubmitting = false;
    this.bookNewViewingModal?.reset();
  }

  protected onBookableSearch(term: string): void {
    this.bookableSearch$.next(term);
  }

  private loadBookableContacts(search: string): void {
    this.bookNewViewingLoading = true;
    this.eventService.bookableViewingContacts(search).subscribe({
      next: (response) => {
        this.bookableContacts = response.data;
        this.bookNewViewingLoading = false;
      },
      error: () => {
        this.bookableContacts = [];
        this.bookNewViewingLoading = false;
        this.toastService.error('Unable to load contacts');
      },
    });
  }

  protected submitBookNewViewing(payload: BookNewViewingPayload): void {
    if (this.bookNewViewingSubmitting) {
      return;
    }
    this.bookNewViewingSubmitting = true;
    this.eventService
      .bookViewing(payload.event_id, payload.show_round_date, payload.show_round_time, payload.show_around_name_id)
      .subscribe({
        next: () => {
          this.closeBookNewViewing();
          this.toastService.success('Viewing booked');
          this.loadBoardData();
        },
        error: () => {
          this.bookNewViewingSubmitting = false;
          this.toastService.error('Unable to book the viewing');
        },
      });
  }

  // Lost capture dialog ------------------------------------------------------
  protected submitMarkAsLost(payload: MarkAsLostPayload): void {
    const eventId = this.pendingLostEventId;
    if (eventId === null || this.lostSubmitting) {
      return;
    }
    this.lostSubmitting = true;
    this.eventService.markAsLost(eventId, payload).subscribe({
      next: () => {
        this.lostSubmitting = false;
        this.closeLostModal();
        this.toastService.success('Event marked as Lost');
        this.loadBoardData();
      },
      error: () => {
        this.lostSubmitting = false;
        this.toastService.error('Unable to mark the event as Lost');
      },
    });
  }

  protected closeLostModal(): void {
    this.lostModalOpen = false;
    this.pendingLostEventId = null;
  }

  // Pencil + Confirmed + demotions ------------------------------------------
  private async confirmMoveToPencil(eventId: number): Promise<void> {
    const proceed = await this.confirmationModal.confirm({
      title: 'Move to Pencil',
      message: 'Hold this date provisionally for the prospect? The card moves to the Pencil column and the date is blocked.',
      confirmLabel: 'Move to Pencil',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'primary',
    });
    if (!proceed) {
      return;
    }
    this.runPencilFlip(eventId, false);
  }

  private runPencilFlip(eventId: number, acknowledgeConcurrentPencil: boolean): void {
    this.eventService.moveToPencil(eventId, acknowledgeConcurrentPencil).subscribe({
      next: () => {
        this.toastService.success('Moved to Pencil');
        this.loadBoardData();
      },
      error: async (error: HttpErrorResponse) => {
        if (error.status === 409 && error.error?.conflict) {
          const conflict = error.error.conflict as { event_id: number; event_reference: string | null; contact_name: string };
          const ref = conflict.event_reference ?? '#' + conflict.event_id;
          const proceed = await this.confirmationModal.confirm({
            title: 'Already has a Pencilled event',
            message: `${conflict.contact_name} already has Pencilled event ${ref} — promote anyway?`,
            confirmLabel: 'Promote anyway',
            cancelLabel: 'Cancel',
            confirmButtonStyle: 'danger',
          });
          if (proceed) {
            this.runPencilFlip(eventId, true);
          }
          return;
        }
        this.toastService.error('Unable to move the event to Pencil');
      },
    });
  }

  private async confirmMoveToConfirmed(eventId: number): Promise<void> {
    // TVG-356: confirming an event is admin-only.
    if (!this.canConfirmEvents) {
      this.toastService.error('Only admins can confirm events');
      return;
    }

    const proceed = await this.confirmationModal.confirm({
      title: 'Confirm booking',
      message: 'Confirm this booking? The card moves to the Confirmed column.',
      confirmLabel: 'Confirm booking',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'success',
    });
    if (!proceed) {
      return;
    }
    this.eventService.updateEventStatus(eventId, 'Confirmed').subscribe({
      next: () => {
        this.toastService.success('Booking confirmed');
        this.loadBoardData();
      },
      error: () => this.toastService.error('Unable to confirm the booking'),
    });
  }

  private demoteToActiveOpportunity(eventId: number, fromKey: BoardColumnKey): void {
    let request$: Observable<unknown>;
    if (fromKey === 'viewing-booked') {
      request$ = this.eventService.cancelViewing(eventId);
    } else if (fromKey === 'pencil-provisional') {
      request$ = this.eventService.releasePencil(eventId);
    } else {
      // Confirmed or Lost → Opportunity. The Event saving() hook clears
      // cancelled_at / lost_at / lost_reason when the event leaves those states.
      request$ = this.eventService.updateEventStatus(eventId, 'Opportunity');
    }

    request$.subscribe({
      next: () => {
        this.toastService.success('Moved to Active Opportunity');
        this.loadBoardData();
      },
      error: () => this.toastService.error('Unable to move the card'),
    });
  }

  drop(event: CdkDragDrop<EventModel[]>, status?: EventModel['status']): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const movedEvent = event.previousContainer.data[event.previousIndex];

      if (status === 'Confirmed') {
        // TVG-356: confirming an event is admin-only.
        if (!this.canConfirmEvents) {
          this.toastService.error('Only admins can confirm events');
          return;
        }

        this.pendingConfirmedDrop = {
          event: movedEvent,
          fromList: event.previousContainer.data,
          toList: event.container.data,
          toIndex: event.currentIndex,
          status,
        };
        this.showConfirmationModal = true;
        return;
      }

      this.transferAndPersist(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex, status);
    }
  }

  moveToBoard(event: EventModel): void {
    this.eventService.updateShowOnBoard(event.id, true).subscribe(() => {
      event.show_on_board = true;
      const swimlane = this.swimlanes.find((s) => s.title === event.status);
      if (swimlane) {
        swimlane.events.push(event);
      }
      this.backlog = this.backlog.filter((e) => e.id !== event.id);
    });
  }

  handleEventClick(event: EventModel) {
    this.router.navigate(['/events', event.id]);
  }

  confirmStatusChange(): void {
    if (!this.pendingConfirmedDrop) {
      return;
    }

    const { event, fromList, toList, toIndex, status } = this.pendingConfirmedDrop;
    const fromIndex = fromList.indexOf(event);
    const safeFromIndex = fromIndex >= 0 ? fromIndex : 0;
    const safeToIndex = Math.min(toIndex, toList.length);

    this.transferAndPersist(fromList, toList, safeFromIndex, safeToIndex, status);

    this.pendingConfirmedDrop = null;
    this.showConfirmationModal = false;
  }

  cancelStatusChange(): void {
    this.pendingConfirmedDrop = null;
    this.showConfirmationModal = false;
  }

  private transferAndPersist(
    fromList: EventModel[],
    toList: EventModel[],
    fromIndex: number,
    toIndex: number,
    status?: EventModel['status'],
  ): void {
    transferArrayItem(fromList, toList, fromIndex, toIndex);
    const movedEvent = toList[toIndex];
    if (status) {
      movedEvent.status = status;
    }
    if (!movedEvent.show_on_board) {
      this.eventService.updateShowOnBoard(movedEvent.id, true).subscribe();
    }
    movedEvent.show_on_board = true;
    this.eventService.updateEventStatus(movedEvent.id, movedEvent.status).subscribe();
  }

  protected readonly PlusCircle = PlusCircle;
  protected readonly ArrowRight = ArrowRight;
  protected readonly SquareChevronRight = SquareChevronRight;
}
